import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

export interface PhraseUsageStats {
  id: string;
  path: string;
  title: string;
  type: 'document' | 'phrase';
  lastAccessed: string;
  lastEdited: string;
  accessCount: number;
  editCount: number;
  referenceCount: number; // How many times this phrase is referenced
  timeSpentMs: number; // Total time spent editing
  score: number; // Calculated score for ranking
  tags: string[];
  metadata?: {
    author?: string;
    description?: string;
  };
}

export interface RecentPhrasesState {
  phrases: Record<string, PhraseUsageStats>;
  sortedIds: string[]; // IDs sorted by score
  lastCleanup: string;
  settings: {
    maxPhrases: number;
    retentionDays: number;
    scoringWeights: {
      recency: number; // Default 0.7
      frequency: number; // Default 0.2
      editActivity: number; // Default 0.1
    };
  };
  // Tracking current session
  currentSession: {
    phraseId?: string;
    startTime?: number;
    edits: number;
  };
}

const initialState: RecentPhrasesState = {
  phrases: {},
  sortedIds: [],
  lastCleanup: new Date().toISOString(),
  settings: {
    maxPhrases: 50,
    retentionDays: 30,
    scoringWeights: {
      recency: 0.7,
      frequency: 0.2,
      editActivity: 0.1,
    },
  },
  currentSession: {
    edits: 0,
  },
};

// Helper function to calculate phrase score
function calculateScore(
  stats: PhraseUsageStats,
  weights: RecentPhrasesState['settings']['scoringWeights']
): number {
  const now = Date.now();

  // Recency score (exponential decay over time)
  const lastAccessTime = new Date(stats.lastAccessed).getTime();
  const hoursSinceAccess = (now - lastAccessTime) / (1000 * 60 * 60);
  const recencyScore = Math.max(0, 100 * Math.exp(-hoursSinceAccess / 24)); // Decay over 24 hours

  // Frequency score (logarithmic growth)
  const frequencyScore = Math.min(100, 20 * Math.log(stats.accessCount + 1));

  // Edit activity score
  const editScore = Math.min(
    100,
    10 * Math.log(stats.editCount + 1) + stats.timeSpentMs / 60000
  );

  // Calculate weighted score
  const totalScore =
    weights.recency * recencyScore +
    weights.frequency * frequencyScore +
    weights.editActivity * editScore;

  // Boost for phrases (as they're more likely to be reused)
  const typeBoost = stats.type === 'phrase' ? 1.2 : 1.0;

  // Boost for frequently referenced items
  const referenceBoost = 1 + stats.referenceCount * 0.05;

  return totalScore * typeBoost * referenceBoost;
}

// Helper to sort phrases by score
function sortPhrasesByScore(
  phrases: Record<string, PhraseUsageStats>
): string[] {
  return Object.entries(phrases)
    .sort(([, a], [, b]) => b.score - a.score)
    .map(([id]) => id);
}

// Helper to clean up old phrases
function cleanupOldPhrases(
  phrases: Record<string, PhraseUsageStats>,
  maxPhrases: number,
  retentionDays: number
): Record<string, PhraseUsageStats> {
  const now = Date.now();
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000;

  // First, remove very old phrases
  const filtered = Object.entries(phrases).filter(([, stats]) => {
    const lastAccessTime = new Date(stats.lastAccessed).getTime();
    return now - lastAccessTime < retentionMs;
  });

  // Then, keep only the top N by score
  const sorted = filtered.sort(([, a], [, b]) => b.score - a.score);
  const kept = sorted.slice(0, maxPhrases);

  return Object.fromEntries(kept);
}

const recentPhrasesSlice = createSlice({
  name: 'recentPhrases',
  initialState,
  reducers: {
    // Track phrase access (viewing)
    trackPhraseAccess: (
      state,
      action: PayloadAction<{
        id: string;
        path: string;
        title: string;
        type: 'document' | 'phrase';
        tags?: string[];
        metadata?: PhraseUsageStats['metadata'];
      }>
    ) => {
      const { id, path, title, type, tags = [], metadata } = action.payload;
      const now = new Date().toISOString();

      if (!state.phrases[id]) {
        // Create new entry
        state.phrases[id] = {
          id,
          path,
          title,
          type,
          tags,
          metadata,
          lastAccessed: now,
          lastEdited: now,
          accessCount: 1,
          editCount: 0,
          referenceCount: 0,
          timeSpentMs: 0,
          score: 0,
        };
      } else {
        // Update existing entry
        state.phrases[id].lastAccessed = now;
        state.phrases[id].accessCount += 1;
        state.phrases[id].title = title; // Update in case it changed
        state.phrases[id].tags = tags;
        state.phrases[id].metadata = metadata;
      }

      // Recalculate score
      state.phrases[id].score = calculateScore(
        state.phrases[id],
        state.settings.scoringWeights
      );

      // Resort
      state.sortedIds = sortPhrasesByScore(state.phrases);
    },

    // Track phrase edit
    trackPhraseEdit: (
      state,
      action: PayloadAction<{
        id: string;
        timeSpentMs?: number;
      }>
    ) => {
      const { id, timeSpentMs = 0 } = action.payload;

      if (state.phrases[id]) {
        state.phrases[id].lastEdited = new Date().toISOString();
        state.phrases[id].editCount += 1;
        state.phrases[id].timeSpentMs += timeSpentMs;

        // Recalculate score
        state.phrases[id].score = calculateScore(
          state.phrases[id],
          state.settings.scoringWeights
        );

        // Resort
        state.sortedIds = sortPhrasesByScore(state.phrases);
      }
    },

    // Track reference usage
    trackPhraseReference: (state, action: PayloadAction<string>) => {
      const id = action.payload;

      if (state.phrases[id]) {
        state.phrases[id].referenceCount += 1;

        // Recalculate score
        state.phrases[id].score = calculateScore(
          state.phrases[id],
          state.settings.scoringWeights
        );

        // Resort
        state.sortedIds = sortPhrasesByScore(state.phrases);
      }
    },

    // Start editing session
    startEditSession: (state, action: PayloadAction<string>) => {
      state.currentSession = {
        phraseId: action.payload,
        startTime: Date.now(),
        edits: 0,
      };
    },

    // End editing session
    endEditSession: (state) => {
      if (state.currentSession.phraseId && state.currentSession.startTime) {
        const timeSpent = Date.now() - state.currentSession.startTime;
        const phraseId = state.currentSession.phraseId;

        if (state.phrases[phraseId]) {
          state.phrases[phraseId].timeSpentMs += timeSpent;
          state.phrases[phraseId].editCount += state.currentSession.edits;
          state.phrases[phraseId].lastEdited = new Date().toISOString();

          // Recalculate score
          state.phrases[phraseId].score = calculateScore(
            state.phrases[phraseId],
            state.settings.scoringWeights
          );
        }
      }

      state.currentSession = { edits: 0 };

      // Resort
      state.sortedIds = sortPhrasesByScore(state.phrases);
    },

    // Track edit in current session
    trackSessionEdit: (state) => {
      state.currentSession.edits += 1;
    },

    // Update scoring weights
    updateScoringWeights: (
      state,
      action: PayloadAction<
        Partial<RecentPhrasesState['settings']['scoringWeights']>
      >
    ) => {
      state.settings.scoringWeights = {
        ...state.settings.scoringWeights,
        ...action.payload,
      };

      // Recalculate all scores
      Object.keys(state.phrases).forEach((id) => {
        state.phrases[id].score = calculateScore(
          state.phrases[id],
          state.settings.scoringWeights
        );
      });

      // Resort
      state.sortedIds = sortPhrasesByScore(state.phrases);
    },

    // Clean up old phrases
    performCleanup: (state) => {
      state.phrases = cleanupOldPhrases(
        state.phrases,
        state.settings.maxPhrases,
        state.settings.retentionDays
      );
      state.sortedIds = sortPhrasesByScore(state.phrases);
      state.lastCleanup = new Date().toISOString();
    },

    // Update settings
    updateSettings: (
      state,
      action: PayloadAction<Partial<RecentPhrasesState['settings']>>
    ) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },

    // Remove specific phrase
    removePhrase: (state, action: PayloadAction<string>) => {
      delete state.phrases[action.payload];
      state.sortedIds = state.sortedIds.filter((id) => id !== action.payload);
    },

    // Clear all phrases
    clearAllPhrases: (state) => {
      state.phrases = {};
      state.sortedIds = [];
      state.lastCleanup = new Date().toISOString();
    },

    // Load persisted state
    loadPersistedState: (
      state,
      action: PayloadAction<Partial<RecentPhrasesState>>
    ) => {
      return {
        ...state,
        ...action.payload,
        currentSession: { edits: 0 }, // Reset session on load
      };
    },
  },
});

// Actions
export const {
  trackPhraseAccess,
  trackPhraseEdit,
  trackPhraseReference,
  startEditSession,
  endEditSession,
  trackSessionEdit,
  updateScoringWeights,
  performCleanup,
  updateSettings,
  removePhrase,
  clearAllPhrases,
  loadPersistedState,
} = recentPhrasesSlice.actions;

// Selectors
export const selectRecentPhrases = (state: RootState) => state.recentPhrases;

export const selectTopPhrases = (state: RootState, limit: number = 10) => {
  const { phrases, sortedIds } = state.recentPhrases;
  return sortedIds.slice(0, limit).map((id) => phrases[id]);
};

export const selectPhrasesByType = (
  state: RootState,
  type: 'document' | 'phrase'
) => {
  const { phrases, sortedIds } = state.recentPhrases;
  return sortedIds
    .filter((id) => phrases[id].type === type)
    .map((id) => phrases[id]);
};

export const selectPhrasesByTag = (state: RootState, tag: string) => {
  const { phrases, sortedIds } = state.recentPhrases;
  return sortedIds
    .filter((id) => phrases[id].tags.includes(tag))
    .map((id) => phrases[id]);
};

export const selectPhraseStats = (state: RootState, phraseId: string) => {
  return state.recentPhrases.phrases[phraseId];
};

// Reducer
export const recentPhrasesReducer = recentPhrasesSlice.reducer;
